import { SizeOptions, SizeOptionsRequest, Size, ServiceLevel } from '../ApiModel';
import { getAppContext } from '../AppContext';
import { numberOfBackups } from './BackupHelper';

export const INCLUDED_INTERFACES = 1;
export const GB_PER_TB = 1000;

type SizeOptionKeysWithNonNullDefaults = 'memory' | 'disk' | 'transfer' | 'ipv4_addresses';

export type SizeOptionsWithDefaultsApplied = {
    [U in SizeOptionKeysWithNonNullDefaults]-?: NonNullable<SizeOptionsRequest[U]>;
} & {
    [P in Exclude<keyof SizeOptionsRequest, SizeOptionKeysWithNonNullDefaults>]: Exclude<SizeOptionsRequest[P], null>;
};

export function getBackupCost(storage_gb: number, retain: number, included: number, sizeOptions: SizeOptions) {
    return storage_gb * Math.max(retain - included, 0) * sizeOptions.backups_cost_per_backup_per_gigabyte;
}

export function getOffsiteBackupsCost(sizeSettings: SizeOptionsWithDefaultsApplied, sizeOptions: SizeOptions) {
    if (!sizeSettings.offsite_backups) return 0;

    const numOfBackups = numberOfBackups(sizeSettings);
    if (numOfBackups === 0) return 0;

    // Offsite backups are charged in two parts, storage and transfer.
    // transfer cost per backup gb is based on the most frequent backup type, while storage cost per backup gb is based on number of backups.

    const frequency_cost_per_gb = sizeSettings.daily_backups
        ? sizeOptions.offsite_backup_frequency_cost.daily_per_gigabyte
        : sizeSettings.weekly_backups
          ? sizeOptions.offsite_backup_frequency_cost.weekly_per_gigabyte
          : sizeOptions.offsite_backup_frequency_cost.monthly_per_gigabyte;

    const backup_storage_per_gb = numOfBackups * sizeOptions.offsite_backups_cost_per_gigabyte;

    return sizeSettings.disk * (backup_storage_per_gb + frequency_cost_per_gb);
}

export function applySizeDefaultsToOptions(
    size: Size,
    selectedOptions: SizeOptionsRequest
): SizeOptionsWithDefaultsApplied {
    // unless the plan includes a backup, we leave them all "undefined" so that they are forced to explicity select 0 backups.
    const hasIncludedBackup =
        size.options.daily_backups + size.options.weekly_backups + size.options.monthly_backups > 0;
    const backupDefaults = hasIncludedBackup
        ? {
              daily_backups: size.options.daily_backups,
              weekly_backups: size.options.weekly_backups,
              monthly_backups: size.options.monthly_backups,
          }
        : {};

    const opts: SizeOptionsWithDefaultsApplied = {
        memory: selectedOptions.memory ?? size.memory,
        disk: selectedOptions.disk ?? size.disk,
        transfer: selectedOptions.transfer ?? size.transfer,

        ipv4_addresses: selectedOptions.ipv4_addresses ?? INCLUDED_INTERFACES,

        daily_backups: selectedOptions.daily_backups ?? backupDefaults.daily_backups,
        weekly_backups: selectedOptions.weekly_backups ?? backupDefaults.weekly_backups,
        monthly_backups: selectedOptions.monthly_backups ?? backupDefaults.monthly_backups,
        offsite_backups: selectedOptions.offsite_backups ?? undefined,
        service_level_id: selectedOptions.service_level_id ?? undefined,
    };

    // if there are no backups, we force offsite to false.
    if (numberOfBackups(opts) === 0) {
        opts.offsite_backups = false;
    }

    return opts;
}

export function getInterfaceCosts(selectedOptions: SizeOptionsWithDefaultsApplied, size: Size): number {
    const interfaces = selectedOptions.ipv4_addresses;
    if (interfaces === 0) return -size.options.discount_for_no_public_ipv4 || 0;
    return (interfaces - INCLUDED_INTERFACES) * size.options.ipv4_addresses_cost_per_address;
}

export function getSizeCost(size: Size, selectedOptions: SizeOptionsRequest, serviceLevel?: ServiceLevel) {
    const sizeOptions = size.options;
    const selected = applySizeDefaultsToOptions(size, selectedOptions);

    const extraMemoryCost = (selected.memory - size.memory) * size.options.memory_cost_per_additional_megabyte;
    const extraStorageCost = (selected.disk - size.disk) * size.options.disk_cost_per_additional_gigabyte;
    const extraTransferCost =
        (selected.transfer - size.transfer) * GB_PER_TB * size.options.transfer_cost_per_additional_gigabyte;
    const backupCosts =
        getBackupCost(selected.disk, selectedOptions.daily_backups ?? 0, sizeOptions.daily_backups, sizeOptions) +
        getBackupCost(selected.disk, selectedOptions.weekly_backups ?? 0, sizeOptions.weekly_backups, sizeOptions) +
        getBackupCost(selected.disk, selectedOptions.monthly_backups ?? 0, sizeOptions.monthly_backups, sizeOptions);

    const interfaceCosts = getInterfaceCosts(selected, size);
    const offsiteCosts = getOffsiteBackupsCost(selected, sizeOptions);

    const serviceLevelCosts = serviceLevel?.price_monthly ?? 0;

    return (
        size.price_monthly +
        extraMemoryCost +
        extraStorageCost +
        extraTransferCost +
        offsiteCosts +
        backupCosts +
        interfaceCosts +
        serviceLevelCosts
    );
}

/// from Swagger:
/// Valid values:
/// - must be a multiple of 128
/// - &amp;gt; 2048MB must be a multiple of 1024
/// - &amp;gt; 16384MB must be a multiple of 2048
/// - &amp;gt; 24576MB must be a multiple of 4096

/// A server's current memory is also valid on its own size, unless it exceeds the size maximum.
export function validMemoryOptions(size: Size, current?: number) {
    const values = [size.memory];
    for (let i = size.memory + 1; i < size.options.memory_max; i++) {
        if (i > 24576) {
            if (i % 4096 === 0) values.push(i);
            continue;
        }

        if (i > 16384) {
            if (i % 2048 === 0) values.push(i);
            continue;
        }

        if (i > 2048) {
            if (i % 1024 === 0) values.push(i);
            continue;
        }

        if (i % 128 === 0) values.push(i);
    }

    if (size.options.memory_max !== size.memory) {
        values.push(size.options.memory_max);
    }

    return withValue(values, current, size.memory, size.options.memory_max);
}

/// Valid values:
/// - must be a multiple of 5
/// - &amp;gt; 30GB must be a multiple of 10
/// - &amp;gt; 200GB must be a multiple of 100
/// - &amp;gt; 2000GB must be a multiple of 1000
/// A server's current transfer (in TB) is also valid on its own size, unless it exceeds the size maximum.
export function validTransferOptions(size: Size, current?: number) {
    const transfer_gb = size.transfer * GB_PER_TB;
    const transfer_max_gb = size.options.transfer_max * GB_PER_TB;
    const values = [transfer_gb];
    for (let i = transfer_gb + 1; i < transfer_max_gb; i++) {
        if (i > 2000) {
            if (i % 1000 === 0) values.push(i);
            continue;
        }

        if (i > 200) {
            if (i % 100 === 0) values.push(i);
            continue;
        }

        if (i > 30) {
            if (i % 10 === 0) values.push(i);
            continue;
        }

        if (i % 5 === 0) values.push(i);
    }

    if (transfer_max_gb !== transfer_gb) {
        values.push(transfer_max_gb);
    }

    const current_gb = current === undefined ? undefined : Math.round(current * GB_PER_TB);
    return withValue(values, current_gb, transfer_gb, transfer_max_gb);
}

/// The plan's own disk, and a server's current disk on its own size, are always valid unless outside the size range.
/// Other valid values for plans that do not provide a value for options.restricted_storage_values_gb:
/// - must be a multiple of 5
/// - &amp;gt; 60GB must be a multiple of 10
/// - &amp;gt; 200GB must be a multiple of 100
export function validStorageOptions(size: Size, current?: number) {
    const { disk_min, disk_max } = size.options;
    return withValue(
        withValue(standardStorageOptions(size), size.disk, disk_min, disk_max),
        current,
        disk_min,
        disk_max
    );
}

function withValue(values: number[], value: number | undefined, min: number, max: number) {
    if (value === undefined || values.includes(value) || value < min || value > max) return values;
    return [...values, value].sort((a, b) => a - b);
}

/// A disk that is not a valid option is rounded up to the next one, so the value displayed, priced and submitted agree.
/// A disk above every option is left for the API to reject rather than silently shrunk.
export function normaliseStorage(size: Size, disk: number, current?: number) {
    const options = validStorageOptions(size, current);
    if (options.includes(disk)) return disk;
    return options.find((o) => o > disk) ?? disk;
}

function standardStorageOptions(size: Size) {
    if (size.options.restricted_disk_values) return size.options.restricted_disk_values;

    const values = [size.options.disk_min];
    for (let i = size.options.disk_min + 1; i < size.options.disk_max; i++) {
        if (i > 200) {
            if (i % 100 === 0) values.push(i);
            continue;
        }

        if (i > 60) {
            if (i % 10 === 0) values.push(i);
            continue;
        }

        if (i % 5 === 0) values.push(i);
    }

    if (size.options.disk_max !== size.options.disk_min) {
        values.push(size.options.disk_max);
    }

    return values;
}

export function hasIncludedBackups(size: Size): boolean {
    return Boolean(size.options.daily_backups || size.options.weekly_backups || size.options.monthly_backups);
}

export function getMaxInterfacesForUser(size: Size): number {
    return Math.min(
        Math.min(size.options.ipv4_addresses_max, getAppContext().customer.item?.additional_ipv4_limit ?? 15),
        15
    );
}
